import { Fragment, useMemo, useState } from 'react';
import { useTheme } from '@/hooks/useTheme';
import { getContrastColor } from '@/utils/colors';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/utils/cn';

/**
 * CreatorToolbar Component
 *
 * Old-system-inspired icon toolbar for PostCreator modal.
 * Uses Material Symbols Outlined (same as old PHP codebase).
 * Small square icon buttons with tooltips, grouped by separators.
 *
 * Features:
 * - Toggle-based UI (click to show/hide sections)
 * - Active state with brand styling
 * - Material Symbols icons matching old codebase
 */

export type CreatorToggle = 'media' | 'carousel' | 'ai' | 'hashtags' | 'labels' | 'schedule';

export interface ToolbarButton<T extends string = CreatorToggle> {
  id: T;
  /** Material Symbols Outlined icon name */
  icon: string;
  tooltip: string;
}

interface CreatorToolbarProps<T extends string = CreatorToggle> {
  activeToggles: Set<T>;
  onToggle: (id: T) => void;
  /** Custom buttons (e.g. the Release drawer). Defaults to the Post Creator buttons. */
  buttons?: ToolbarButton<T>[];
  /** 'compact' = one step smaller buttons/icons and a tighter gap. Defaults to 'default'. */
  size?: 'default' | 'compact';
  /** Ids after which a separator is drawn (custom buttons only). */
  separatorAfter?: Set<T>;
  disabled?: boolean;
  className?: string;
  showCarousel?: boolean;
  /** Set of toggle IDs that should be disabled (overrides global disabled) */
  disabledToggles?: Set<T>;
  /** Set of toggle IDs that should be completely hidden (not rendered) */
  hiddenToggles?: Set<T>;
}

// Icons match old PHP codebase exactly (Material Symbols Outlined)
const TOOLBAR_BUTTONS: ToolbarButton<CreatorToggle>[] = [
  { id: 'media', icon: 'photo_camera', tooltip: 'Add media' },
  { id: 'carousel', icon: 'filter_6', tooltip: 'Create carousel' },
  { id: 'hashtags', icon: 'tag', tooltip: 'Hashtags' },
  { id: 'labels', icon: 'label', tooltip: 'Labels' },
  { id: 'schedule', icon: 'rocket_launch', tooltip: 'Schedule & Shoot' },
];

// Separator after these button ids
const SEPARATOR_AFTER_ID: Set<CreatorToggle> = new Set(['carousel', 'media']);

// CSS defaults from index.css (:root) used if the variable cannot be read
const FALLBACK_SECONDARY = '#69C7C1';
const FALLBACK_GRAY_2 = '#DDE2E5';

/** Read a resolved CSS variable; accepts only #RRGGBB, otherwise returns the fallback. */
const readCssColor = (name: string, fallback: string): string => {
  if (typeof document === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback;
};

export const CreatorToolbar = <T extends string = CreatorToggle>({
  activeToggles,
  onToggle,
  buttons,
  size = 'default',
  separatorAfter,
  disabled = false,
  className,
  showCarousel = false,
  disabledToggles,
  hiddenToggles,
}: CreatorToolbarProps<T>) => {
  const { branding } = useTheme();
  const [hoveredId, setHoveredId] = useState<T | null>(null);

  // --bs-secondary / --bs-gray-2 are injected by ThemeProvider (whitelabel-aware) right
  // after branding state updates, so re-read them whenever branding changes.
  const { secondaryText, grayText } = useMemo(() => {
    const secondary = readCssColor('--bs-secondary', FALLBACK_SECONDARY);
    const gray2 = readCssColor('--bs-gray-2', FALLBACK_GRAY_2);
    return { secondaryText: getContrastColor(secondary), grayText: getContrastColor(gray2) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branding]);

  const isCustom = buttons !== undefined;
  const compact = size === 'compact';
  const allButtons = (buttons ?? TOOLBAR_BUTTONS) as ToolbarButton<T>[];
  const separators = (separatorAfter ?? SEPARATOR_AFTER_ID) as Set<string>;
  const visibleButtons = allButtons.filter(
    (b) => (isCustom || b.id !== 'carousel' || showCarousel) && !hiddenToggles?.has(b.id)
  );

  return (
    <div className={cn('flex items-center justify-center lg:justify-start w-full', className)}>
      <div className={cn('flex items-center', compact ? 'gap-3 sm:gap-2.5' : 'gap-2.5 sm:gap-3.5')}>
        {visibleButtons.map((button, index) => {
          const isActive = activeToggles.has(button.id);
          const isButtonDisabled = disabled || disabledToggles?.has(button.id);
          const isLastVisible = index === visibleButtons.length - 1;

          return (
            <Fragment key={button.id}>
              <Tooltip content={button.tooltip} side="bottom">
                <button
                  type="button"
                  onClick={() => onToggle(button.id)}
                  disabled={isButtonDisabled}
                  className={cn(
                    compact ? 'p-[6px] sm:p-[7px] md:p-2' : 'p-[9px] sm:p-2.5 md:p-3',
                    'flex items-center justify-center',
                    // 44px touch target below sm without changing the visual size
                    "max-sm:relative max-sm:before:absolute max-sm:before:left-1/2 max-sm:before:top-1/2 max-sm:before:h-[44px] max-sm:before:w-[44px] max-sm:before:-translate-x-1/2 max-sm:before:-translate-y-1/2 max-sm:before:content-['']",
                    'rounded-md',
                    'transition-colors duration-200',
                    'focus:outline-none',
                    !isActive && 'bg-bs-gray-2 hover:bg-bs-secondary',
                    isActive && 'bg-bs-secondary',
                    isButtonDisabled && 'opacity-50 cursor-not-allowed'
                  )}
                  style={{
                    color: isActive || (hoveredId === button.id && !isButtonDisabled) ? secondaryText : grayText,
                  }}
                  onMouseEnter={() => setHoveredId(button.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  aria-label={button.tooltip}
                  aria-pressed={isActive}
                >
                  <span
                    className={cn(
                      'material-symbols-outlined leading-none',
                      compact
                        ? '!text-[16px] sm:!text-[17px] md:!text-[18px]'
                        : '!text-[18px] sm:!text-[20px] md:!text-[21px]'
                    )}
                  >
                    {button.icon}
                  </span>
                </button>
              </Tooltip>

              {!isLastVisible &&
                separators.has(button.id) &&
                (isCustom || ((button.id !== 'media' || !showCarousel) && (button.id !== 'carousel' || showCarousel))) && (
                <div className="w-px h-4 bg-black/60" aria-hidden="true" />
              )}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};
